import React from 'react';
import { useLocation } from 'react-router';
import { Pagination } from '@mui/material';
import qs from 'qs';

import { QS_STRINGIFY_OPTIONS } from '@api/constants/other';
import { analytics } from 'src/utils/analytics';
import { imgixUrl } from 'src/utils/imgix-url';
import { getCroppedPhoto } from 'src/utils/get-cropped-photo';
import { useAppSelector } from 'src/hooks/store';
import { selectConfig } from 'src/store/config/selectors';
import { ListItem } from './ListItem';
import { useSearch, PAGE_SIZE } from '../FilterSection/Search/Context';
import { Loader } from './Loader';

import css from './List.module.scss';

export const List = () => {
  const location = useLocation();
  const search = useSearch();
  const config = useAppSelector(selectConfig);
  const items = search.result?.success ? search.result.data.items : [];
  const totalItems = search.result?.success ? search.result.data.page.total : 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const onScrollClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const handleChange = (e: React.ChangeEvent<unknown>, value: number) => {
    search.formik.setFieldValue('page', value);
    search.formik.submitForm();
    onScrollClick();

    analytics.gtag.event('Clicking on page', { CUSTOM_PARAMETER: `page ${value}` });
  };

  const getAmountText = (value: number | undefined) => {
    return `נמצאו ${value || 0} תוצאות עבור:`;
  };

  const renderSearchParams = () => {
    const urlParams = qs.parse(location.search.replace('?', ''), QS_STRINGIFY_OPTIONS) as any;

    const query = urlParams?.query as string;
    const battle = config.battles[urlParams?.filters?.battle as string];
    const division = config.divisions[urlParams?.filters?.division as string];
    const unit = config.units[urlParams?.filters?.unit as string];
    const name = config.soldiers[urlParams?.filters?.name as string];
    const valuesToRender: string[] = [];

    if (query) valuesToRender.push(query);
    if (battle?.title) valuesToRender.push(battle?.title);
    if (division?.title) valuesToRender.push(division?.title);
    if (unit?.title) valuesToRender.push(unit?.title);
    if (name?.name) valuesToRender.push(name?.name);

    return (
      <div className={css.searchParams}>
        <div className={css.searchParamsItem}>{valuesToRender.map((v) => `"${v}"`).join(', ')}</div>
      </div>
    );
  };

  return (
    <div className={css.listWrapper}>
      {search.isSearchActive && (
        <>
          {totalItems > 0 && (
            <div className={css.resultsAmountWrapper}>
              <div className={css.resultsAmount}>
                <span>{getAmountText(totalItems)}</span>
              </div>
              {renderSearchParams()}
            </div>
          )}
        </>
      )}
      <div className={css.list}>
        {search.isFetching ? (
          <>
            <Loader />
            <Loader />
            <Loader />
            <Loader />
          </>
        ) : (
          <>
            {items.map((item) => {
              const cropped = item.memory?.photo
                ? getCroppedPhoto(item.memory?.photo, 'mainPhoto', item.memory?.metadata)
                : item.memory?.photos?.[0]?.url;

              const imgixParams = {
                w: 330,
                h: 207,
                auto: 'format,compress',
                fit: 'facearea,crop',
                facepad: 100,
              };

              let imgUrl = imgixUrl(cropped || item.memory?.photo || item.memory?.photos?.[0]?.url, imgixParams);
              const soldierData = item.soldier;
              const indicators = [soldierData.division, soldierData.unit, soldierData.battle].filter(
                (item) => !!item,
              ) as string[];

              if (item.memory?.photos) {
                if (item.memory?.photo && item.memory.metadata?.hidePhoto) {
                  const hide = item.memory.metadata?.hidePhoto.find((photo) => photo === item.memory?.photo);

                  if (hide) imgUrl = '';
                }
              }

              return (
                <ListItem
                  id={item._id.toString()}
                  title={item.soldier.name}
                  subtitle={item.memory?.story}
                  tags={indicators}
                  imgUrl={imgUrl}
                  key={item._id.toString()}
                />
              );
            })}
          </>
        )}
      </div>
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={search.formik.values.page}
          onChange={handleChange}
          className={css.pageSwitcher}
          shape="rounded"
          size="small"
        />
      )}
      <div id="form" className={css.scrollPoint} />
    </div>
  );
};
